/**
 * Resolved-type → typed-form switch.
 *
 * Centralised mapping from an encounter-type code to the typed form that
 * owns it. Used by:
 *  - `EncounterTypeDispatcher` for deterministic dispatch (existing
 *    encounter tag, activity-map single-string, single-element
 *    `restrictToEncounterTypes`).
 *  - `UnifiedTypePickerForm` after the user picks a type from the picker.
 *
 * Keeping the switch in one place ensures both call sites mount the same
 * typed form for the same code, with the same prop bag.
 *
 * @agentic connections
 */

import { Alert, Stack, Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { forwardRef } from "react";
import type { Encounter } from "@medplum/fhirtypes";
import { isLegacyEncounterType } from "@benchmark/core/constants";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";

import type { EncounterActivityContext } from "@benchmark/core/types";

import { CaseManagementInteractionForm } from "./forms/CaseManagementInteractionForm";
import { FollowUpInteractionForm } from "./forms/FollowUpInteractionForm";
import { IntakeInteractionForm } from "./forms/IntakeInteractionForm";
import { DischargeInteractionForm } from "./forms/DischargeInteractionForm";
import { GeneralInteractionForm } from "./forms/GeneralInteractionForm";
import { LegacyEncounterReadOnlyForm } from "./forms/LegacyEncounterReadOnlyForm";
import type { EncounterFormRef } from "./EncounterFormShell";

/**
 * Shared props passed to every typed form. Mirrors `EncounterDispatchProps`
 * minus the resolved type — kept structurally identical so call sites can
 * spread their existing prop bags into this switch.
 *
 * @agentic connections
 */
export type ResolveTypedFormProps = {
  pickedType: string;
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Resolves a known encounter-type code to its typed form. When the code is
 * unrecognised — or names a legacy type without an `encounter` to edit —
 * renders a defensive alert so a misdispatched code is visible rather than
 * silently rendering nothing.
 *
 * @agentic connections
 */
export const ResolveTypedForm = forwardRef<
  EncounterFormRef,
  ResolveTypedFormProps
>(function ResolveTypedForm({ pickedType, ...rest }, ref) {
  if (pickedType === "general-interaction") {
    return <GeneralInteractionForm ref={ref} {...rest} />;
  }

  if (pickedType === "case-management-interaction") {
    return <CaseManagementInteractionForm ref={ref} {...rest} />;
  }

  if (pickedType === "discharge-interaction") {
    return <DischargeInteractionForm ref={ref} {...rest} />;
  }

  if (
    pickedType === "clinical-follow-up-interaction" ||
    pickedType === "cocm-follow-up-interaction" ||
    pickedType === "unbilled-follow-up-interaction"
  ) {
    return <FollowUpInteractionForm ref={ref} {...rest} />;
  }

  if (
    pickedType === "clinical-intake-interaction" ||
    pickedType === "cocm-intake-interaction" ||
    pickedType === "unbilled-intake-interaction"
  ) {
    return <IntakeInteractionForm ref={ref} {...rest} />;
  }

  if (isLegacyEncounterType(pickedType) && rest.encounter) {
    return (
      <LegacyEncounterReadOnlyForm
        ref={ref}
        patientId={rest.patientId}
        encounter={rest.encounter}
        onSuccess={rest.onSuccess}
        onCancel={rest.onCancel}
      />
    );
  }

  return (
    <Stack gap="md" data-testid="encounter-type-dispatcher-error">
      <Alert
        color="red"
        variant="light"
        icon={<IconAlertTriangle size={18} />}
        title="Unable to open this encounter"
      >
        <Text size="sm">
          We could not determine the type of this encounter. Please refresh and
          try again, or contact support if the issue persists.
        </Text>
      </Alert>
    </Stack>
  );
});
